import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useApplicationChat } from '../context';
import { useWorkflowOperationController } from './useSubmission.js';
import { useInputResponse } from './useInputResponse';
import { useActionInputSubmission } from './useActionInput.js';

interface OperationsView {
  workflow: ReturnType<typeof useWorkflowOperationController>;
  actionInput: ReturnType<typeof useActionInputSubmission>;
  inputResponse: ReturnType<typeof useInputResponse>;
}
const OperationsContext = createContext<OperationsView | null>(null);

/** Receipts and pending commands outlive presentation replacement. */
export const OperationsProvider = ({ children }: { children?: ReactNode }) => {
  const { realApi, viewModel, apiReadinessGate, workspaceLocation } = useApplicationChat();
  const input = {
    activeSessionId: viewModel.sessionList.activeSessionId,
    runAPI: realApi.runAPI,
    onRefreshSession: viewModel.session.onRefresh,
    onRefreshSessions: viewModel.toolbar.onRefreshSessions,
  };
  const workflow = useWorkflowOperationController({ ...input, renderedTimelineItems: viewModel.timeline.items });
  const actionInput = useActionInputSubmission(input);
  const inputResponse = useInputResponse({ activeSessionId: input.activeSessionId,
    projectId: workspaceLocation?.effectiveProjectId, userId: viewModel.composer.userId, ready: !apiReadinessGate.blocked,
    projection: viewModel.composer.inputProjection, content: viewModel.composer.value,
    loading: viewModel.composer.loading, submitting: viewModel.composer.submitting,
    actionInput, onSend: viewModel.composer.onSend, onRespondInput: viewModel.composer.onRespondInput,
    onRetry: viewModel.toolbar.onRetryLastSend,
  });
  const value = useMemo(() => ({ workflow, actionInput, inputResponse }), [workflow, actionInput, inputResponse]);
  return <OperationsContext.Provider value={value}>{children}</OperationsContext.Provider>;
};
export const useOperations = (): OperationsView => {
  const value = useContext(OperationsContext);
  if (!value) throw new Error('Chat operations require the application provider');
  return value;
};
